/**
 * Trip batch entry -- a page of paper slips, keyed without the mouse.
 *
 * The shape of this screen comes from how the work actually arrives: one
 * office clerk, 100+ slips a month, all for the same customer and often
 * the same route, typed in sittings rather than one at a time.
 *
 * So the customer and the date sit ABOVE the grid, filled once, and the
 * grid carries only what changes per slip. A customer column repeated
 * down thirty identical rows is thirty chances to pick the wrong one.
 *
 * Tab moves along a row and wraps to the next, adding a row when it runs
 * off the end, so a clerk never reaches for the mouse to continue. The
 * rate pre-fills from what this customer last paid on this route, and is
 * always overtypable -- the client's rates are negotiated per trip.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert, Box, Button, Chip, MenuItem, Paper, Snackbar, Stack, TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  addTrips, fetchDrivers, fetchRate, fetchRoutes, fetchVehicles,
} from '../api/endpoints'
import { errorMessage } from '../api/client'
import { P, type TripIn } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { useHotkeys } from '../hotkeys/HotkeyProvider'
import { c, tokens } from '../theme'
import Keycap from '../components/Keycap'
import ScreenTitle from '../components/ScreenTitle'
import { useUnsavedChanges } from '../store/UnsavedChanges'
import { toHijri } from '../reports/hijri'
import LedgerPicker from '../vouchers/LedgerPicker'
import CreatePartyDialog from '../vouchers/CreatePartyDialog'
import {
  QuickDriverDialog, QuickRouteDialog, QuickVehicleDialog,
} from './QuickMasters'
import { normalise, sanitize } from '../vouchers/money'
import { today } from '../dates'
import { sanitizeWeight, useTripRef } from './tripRef'

/**
 * Column widths. Narrow fields stay narrow so the eye can scan a column.
 *
 * Date leads, because that is the order the slip reads in and because a
 * clerk scanning for the one row they mistyped looks down the left edge.
 *
 * Driver sits second-to-last, after the rate. It is optional -- the
 * client's slips do not always carry it -- so it is placed past
 * everything required: a clerk who skips it never tabs through it on
 * the way to anything that matters.
 */
const GRID = '118px 112px 1fr 108px 112px 92px 92px 100px 106px 1fr'
/** Below this the two flexible columns would collapse to nothing. */
const GRID_MIN = 1180

interface RowState {
  key: number
  date: string
  routeId: string
  vehicle: string
  container: string
  doNo: string
  wayBill: string
  /** Kilograms, as typed. Used instead of the way bill by a weight company. */
  weight: string
  rate: string
  driver: string
  remarks: string
}

let nextKey = 1
/**
 * A new row inherits the batch date rather than today's.
 *
 * Slips arrive in bundles by day, so the clerk sets the date once at the
 * top and every row follows -- but a row can be overtyped, which is the
 * whole point: Sunday's and Monday's slips are now one save, not two.
 */
const blankRow = (date: string): RowState => ({
  key: nextKey++, date, routeId: '', vehicle: '', container: '', doNo: '',
  wayBill: '', weight: '', rate: '', driver: '', remarks: '',
})



/** Has anything been keyed into the grid? The date alone does not count:
 *  every blank row carries one. */
export function hasWork(rows: Omit<RowState, 'key' | 'date'>[]): boolean {
  return rows.some((r) =>
    [r.routeId, r.vehicle, r.container, r.doNo, r.wayBill, r.weight, r.rate,
     r.driver, r.remarks].some((v) => v.trim() !== ''))
}

function Cell({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ borderInlineEnd: `1px solid ${c.divider}`,
               display: 'flex', alignItems: 'center' }}>
      {children}
    </Box>
  )
}

/** A bare input that inherits the grid's typography. Matches the voucher
 *  grid, which is hand-built for the same reason: MUI's TextField carries
 *  far too much chrome for a dense data entry surface. */
function GridInput({
  value, onChange, label, placeholder, mono, align, inputRef, onKeyDown,
  type,
}: {
  value: string
  onChange: (v: string) => void
  /** `date` gives the clerk the browser's own date editor in-cell. */
  type?: 'date'
  /** Accessible name. A bare cell in a grid has no visible label of its
   *  own -- the column heading is not associated with it -- so a screen
   *  reader would announce thirty unnamed text boxes. */
  label: string
  placeholder?: string
  mono?: boolean
  align?: 'right'
  inputRef?: (el: HTMLInputElement | null) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
}) {
  return (
    <input
      ref={inputRef}
      type={type}
      aria-label={label}
      className={mono ? 'num' : undefined}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      style={{
        width: '100%', height: tokens.rowHeight - 8, padding: '0 8px',
        border: '1px solid transparent', borderRadius: 4,
        background: 'transparent', font: 'inherit',
        fontFamily: mono ? tokens.mono : 'inherit',
        fontVariantNumeric: mono ? 'tabular-nums' : undefined,
        textAlign: align, color: c.ink, outline: 'none',
      }}
    />
  )
}

export default function TripEntry() {
  const { t, i18n } = useTranslation()
  const refCol = useTripRef()
  const { can } = useAuth()
  const qc = useQueryClient()

  const [date, setDate] = useState(today())
  const [customer, setCustomer] = useState('')
  const [rows, setRows] = useState<RowState[]>(
    () => [blankRow(today()), blankRow(today())])
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  /* The batch date, readable from callbacks that must not change identity
   * every time it is edited -- addRow is registered as a hotkey. */
  const dateRef = useRef(date)
  dateRef.current = date

  /**
   * Changing the batch date re-dates the rows that still carry the old
   * one, and leaves overtyped rows alone.
   *
   * The clerk who notices halfway down a page that they are keying
   * Sunday's slips under Monday's date fixes it once at the top. The two
   * rows they had already corrected by hand are not undone.
   */
  const setBatchDate = useCallback((next: string) => {
    const prev = dateRef.current
    setDate(next)
    setRows((rs) => rs.map((r) => (r.date === prev ? { ...r, date: next } : r)))
  }, [])

  const cells = useRef<Record<string, HTMLInputElement | null>>({})
  const mayEnter = can(P.transportEntry)

  /* A half-keyed bundle of slips is an afternoon's work: leaving the
   * screen with any row filled in asks first (decision #27). The date
   * alone is not work -- every blank row carries one. */
  const dirty = useMemo(() => hasWork(rows), [rows])
  useUnsavedChanges(dirty)

  const vehicles = useQuery({ queryKey: ['vehicles'],
    queryFn: () => fetchVehicles() })
  const routes = useQuery({ queryKey: ['routes'], queryFn: () => fetchRoutes() })
  const drivers = useQuery({ queryKey: ['drivers'],
    queryFn: () => fetchDrivers() })

  /*
   * Create-on-the-fly.
   *
   * `pending` remembers which cell asked, so a new master is selected
   * into the row that was waiting for it and the cursor goes back
   * there. Without that the clerk adds a truck and then has to find
   * their place in fifteen rows, which is most of the cost of leaving
   * the screen in the first place.
   */
  const [quick, setQuick] =
    useState<null | 'customer' | 'vehicle' | 'route' | 'driver'>(null)
  const pending = useRef<number | null>(null)

  const openQuick = useCallback(
    (what: 'customer' | 'vehicle' | 'route' | 'driver', rowKey?: number) => {
      pending.current = rowKey ?? null
      setQuick(what)
    }, [])

  const backToRow = useCallback((field: string) => {
    const key = pending.current
    pending.current = null
    if (key == null) return
    requestAnimationFrame(() => cells.current[`${key}:${field}`]?.focus())
  }, [])


  // -- rows ---------------------------------------------------------------

  const setRow = useCallback((key: number, patch: Partial<RowState>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  }, [])

  const addRow = useCallback(() => {
    const row = blankRow(dateRef.current)
    setRows((prev) => [...prev, row])
    // Focus the new row once React has painted it.
    requestAnimationFrame(() => cells.current[`${row.key}:route`]?.focus())
    return row
  }, [])

  const removeRow = useCallback((key: number) => {
    setRows((prev) => (prev.length > 1
      ? prev.filter((r) => r.key !== key)
      : [blankRow(dateRef.current)]))
  }, [])

  /**
   * Pre-fill the rate from what this customer last paid on this route.
   *
   * Only when the cell is still empty. Overwriting a rate the clerk has
   * already typed, because they corrected the route afterwards, would
   * silently change the price of the trip.
   */
  const suggestRate = useCallback(async (key: number, routeId: string) => {
    if (!customer || !routeId) return
    const row = rows.find((r) => r.key === key)
    if (row && row.rate.trim()) return
    try {
      const { rate } = await fetchRate(customer, Number(routeId))
      if (rate) setRow(key, { rate: normalise(rate) })
    } catch {
      // A missing suggestion is not an error worth interrupting entry for.
    }
  }, [customer, rows, setRow])

  // -- save ---------------------------------------------------------------

  const filled = useMemo(
    () => rows.filter((r) => r.vehicle.trim() && r.rate.trim()),
    [rows])

  const save = useMutation({
    mutationFn: async () => {
      if (!customer) throw new Error(t('trip.needCustomer'))
      if (filled.length === 0) throw new Error(t('trip.needRows'))
      // A row whose date was cleared must not quietly post under the
      // batch date: that is a trip filed on a day it did not happen.
      if (filled.some((r) => !r.date)) throw new Error(t('trip.needDate'))
      const payload: TripIn[] = filled.map((r) => ({
        trip_date: r.date,
        customer_code: customer,
        vehicle_plate: r.vehicle.trim(),
        rate: normalise(r.rate),
        route_id: r.routeId ? Number(r.routeId) : null,
        container_no: r.container.trim() || null,
        do_number: r.doNo.trim() || null,
        // Only the column this company uses is sent. A way bill typed
        // before the setting changed must not ride along unseen.
        ...(refCol === 'WEIGHT'
          ? { weight_kg: r.weight.trim() || null }
          : { way_bill: r.wayBill.trim() || null }),
        driver_name: r.driver.trim() || null,
        remarks: r.remarks.trim() || null,
      }))
      return addTrips(payload)
    },
    onSuccess: (res) => {
      setError(null)
      setSaved(t('trip.saved', { count: res.count }))
      setRows([blankRow(dateRef.current), blankRow(dateRef.current)])
      void qc.invalidateQueries({ queryKey: ['trips'] })
      requestAnimationFrame(() => {
        const first = rows[0]
        cells.current[`${first?.key}:route`]?.focus()
      })
    },
    onError: (e) => setError(errorMessage(e)),
  })

  /**
   * Which row a create-on-the-fly key belongs to.
   *
   * Recorded by the row itself on focusin, not derived from
   * document.activeElement. MUI's Select keeps its ref on a hidden
   * input and focuses a different node, so matching activeElement
   * against the cell refs missed every dropdown -- and a clerk pressing
   * Alt+R with the cursor in row 1 got the new route dropped into the
   * last row instead.
   *
   * A ref, not state: it changes on every focus move and nothing
   * renders from it.
   */
  const activeRow = useRef<number | null>(null)
  const focusedRowKey = useCallback(
    (): number | undefined => activeRow.current ?? rows[0]?.key, [rows])

  useHotkeys('trip-entry', [
    { combo: 'alt+a', label: t('entry.addLine'), allowInInput: true,
      handler: () => { addRow() } },
    { combo: 'ctrl+enter', label: t('trip.save'), allowInInput: true,
      handler: () => { if (!save.isPending) save.mutate() } },
    // Create-on-the-fly. allowInInput, because the whole point is to
    // fire them from inside a half-typed row.
    { combo: 'alt+c', label: t('quick.newCustomer'), allowInInput: true,
      handler: () => openQuick('customer') },
    { combo: 'alt+v', label: t('quick.newVehicle'), allowInInput: true,
      handler: () => openQuick('vehicle', focusedRowKey()) },
    { combo: 'alt+r', label: t('quick.newRoute'), allowInInput: true,
      handler: () => openQuick('route', focusedRowKey()) },
    { combo: 'alt+n', label: t('quick.newDriver'), allowInInput: true,
      handler: () => openQuick('driver', focusedRowKey()) },
  ])

  useEffect(() => {
    if (!customer) return
    // Once the customer is chosen the clerk's hands belong in the grid.
    requestAnimationFrame(() => cells.current[`${rows[0]?.key}:route`]?.focus())
    // Only on a customer change, not on every row edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer])

  if (!mayEnter) {
    return <Alert severity="warning">{t('trip.noPermission')}</Alert>
  }

  const routeOptions = routes.data ?? []
  const vehicleOptions = vehicles.data ?? []

  return (
    <Stack spacing={2}>
      <Paper variant="outlined">
        <ScreenTitle titleKey="nav.tripEntry" sub={t('trip.entrySub')}
          right={<Keycap combo="ctrl+enter" />} />

        {/* Filled once per batch, not once per slip. */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
          sx={{ px: 2.5, pt: 1, pb: 2, alignItems: { sm: 'flex-start' } }}>
          <Box>
            <TextField label={t('trip.batchDate')} type="date" size="small"
              value={date} onChange={(e) => setBatchDate(e.target.value)}
              sx={{ width: 180 }} InputLabelProps={{ shrink: true }}
              helperText={t('trip.batchDateHint')} />
            {date && (
              <Typography sx={{ fontSize: 12, color: c.inkMuted, mt: 0.25, px: 1.75 }}>
                {toHijri(date, i18n.language)}</Typography>
            )}
          </Box>
          <Box sx={{ position: 'relative', minWidth: 260, flex: 1 }}>
            <Typography sx={{ fontSize: 11, color: c.inkMuted, mb: 0.5 }}>
              {t('trip.customer')}
            </Typography>
            {/* Debtors only: a trip is billed to a customer, and
                offering the whole chart here invites a trip booked
                against the fuel account. */}
            <LedgerPicker
              value={customer}
              onChange={(code) => setCustomer(code)}
              groups={['DEBTORS']}
              placeholder={t('trip.customerPlaceholder')}
            />
          </Box>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mx: 2, mb: 2 }}>{error}</Alert>
        )}

        {/* Grid. Scrolls sideways inside its card on a narrow screen
            rather than pushing the whole page wider than the phone. */}
        <Box sx={{ borderTop: `1px solid ${c.border}`, overflowX: 'auto' }}>
          <Box sx={{
            display: 'grid', gridTemplateColumns: `${GRID} 44px`, minWidth: GRID_MIN,
            bgcolor: c.surfacePage, borderBottom: `1px solid ${c.border}`,
          }}>
            {/* Way bill is here because the printed invoice has a Way
                Bill column. A column on the customer's document that the
                clerk has no field to fill is a column that is always
                blank. */}
            {[t('entry.date'), t('trip.route'), t('trip.fromTo'), t('trip.vehicle'),
              t('trip.container'), t('trip.doNumber'),
              refCol === 'WEIGHT' ? t('trip.weightKg') : t('trip.wayBill'),
              t('trip.rate'), t('trip.driver'), t('trip.remarks'),
              ''].map((h, i) => (
              <Box key={i} sx={{ px: 1, py: 0.75 }}>
                <span className="section-label">{h}</span>
              </Box>
            ))}
          </Box>

          {rows.map((r) => {
            const route = routeOptions.find((x) => String(x.id) === r.routeId)
            return (
              <Box key={r.key}
                onFocusCapture={() => { activeRow.current = r.key }}
                sx={{
                  display: 'grid', gridTemplateColumns: `${GRID} 44px`, minWidth: GRID_MIN,
                  borderBottom: `1px solid ${c.divider}`,
                  minHeight: tokens.rowHeight,
                }}>
                {/* Pre-filled from the batch date, overtypable here. A
                    bundle of slips spanning two days is one save. */}
                <Cell>
                  <GridInput value={r.date} type="date" mono
                    label={t('entry.date')}
                    inputRef={(el) => { cells.current[`${r.key}:date`] = el }}
                    onChange={(v) => setRow(r.key, { date: v })} />
                </Cell>

                <Cell>
                  <TextField select size="small" variant="standard"
                    value={r.routeId} fullWidth
                    InputProps={{ disableUnderline: true }}
                    inputRef={(el) => { cells.current[`${r.key}:route`] = el }}
                    onChange={(e) => {
                      setRow(r.key, { routeId: e.target.value })
                      void suggestRate(r.key, e.target.value)
                    }}
                    sx={{ '& .MuiSelect-select': { py: 0.6, px: 1, fontSize: 13 } }}>
                    <MenuItem value="">—</MenuItem>
                    {routeOptions.map((x) => (
                      <MenuItem key={x.id} value={String(x.id)}>
                        {x.from_place} → {x.to_place}
                      </MenuItem>
                    ))}
                  </TextField>
                </Cell>

                <Cell>
                  <Typography sx={{ px: 1, fontSize: 13, color: c.inkMuted }}>
                    {route ? `${route.from_place} → ${route.to_place}` : '—'}
                  </Typography>
                </Cell>

                <Cell>
                  <TextField select size="small" variant="standard"
                    value={r.vehicle} fullWidth
                    InputProps={{ disableUnderline: true }}
                    inputRef={(el) => { cells.current[`${r.key}:vehicle`] = el }}
                    onChange={(e) => setRow(r.key, { vehicle: e.target.value })}
                    sx={{ '& .MuiSelect-select': { py: 0.6, px: 1,
                                                   fontSize: 13,
                                                   fontFamily: tokens.mono } }}>
                    <MenuItem value="">—</MenuItem>
                    {vehicleOptions.map((v) => (
                      <MenuItem key={v.id} value={v.plate_no}>
                        {v.plate_no}
                        {v.kind === 'SUBCONTRACTED' && (
                          <Chip size="small" label={t('trip.hired')}
                            sx={{ ml: 1, height: 16, fontSize: 9,
                                  bgcolor: c.warningSoft, color: c.warningInk }} />
                        )}
                      </MenuItem>
                    ))}
                  </TextField>
                </Cell>

                <Cell>
                  <GridInput value={r.container} mono
                    label={t('trip.container')}
                    onChange={(v) => setRow(r.key, { container: v.toUpperCase() })} />
                </Cell>

                <Cell>
                  <GridInput value={r.doNo} mono label={t('trip.doNumber')}
                    onChange={(v) => setRow(r.key, { doNo: v })} />
                </Cell>

                <Cell>
                  {refCol === 'WEIGHT' ? (
                    <GridInput value={r.weight} mono align="right"
                      label={t('trip.weightKg')}
                      onChange={(v) => setRow(r.key, { weight: sanitizeWeight(v) })} />
                  ) : (
                    <GridInput value={r.wayBill} mono label={t('trip.wayBill')}
                      onChange={(v) => setRow(r.key, { wayBill: v })} />
                  )}
                </Cell>

                <Cell>
                  <GridInput value={r.rate} mono align="right"
                    label={t('trip.rate')}
                    onChange={(v) => setRow(r.key, { rate: sanitize(v) })}
                    onKeyDown={(e) => {
                      // Enter at the end of a row starts the next slip.
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        setRow(r.key, { rate: normalise(r.rate) })
                        const isLast = rows[rows.length - 1].key === r.key
                        if (isLast) addRow()
                        else {
                          const i = rows.findIndex((x) => x.key === r.key)
                          cells.current[`${rows[i + 1].key}:route`]?.focus()
                        }
                      }
                    }} />
                </Cell>

                {/* Optional, and it stays optional: a free dash means
                    "not recorded", which is the honest state for a slip
                    that did not carry a name. */}
                <Cell>
                  <TextField select size="small" variant="standard"
                    value={r.driver} fullWidth
                    InputProps={{ disableUnderline: true }}
                    inputRef={(el) => { cells.current[`${r.key}:driver`] = el }}
                    onChange={(e) => setRow(r.key, { driver: e.target.value })}
                    sx={{ '& .MuiSelect-select': { py: 0.6, px: 1,
                                                   fontSize: 13 } }}>
                    <MenuItem value="">—</MenuItem>
                    {(drivers.data ?? []).map((d) => (
                      <MenuItem key={d.id} value={d.name}>{d.name}</MenuItem>
                    ))}
                  </TextField>
                </Cell>

                <Cell>
                  <GridInput value={r.remarks} label={t('trip.remarks')}
                    onChange={(v) => setRow(r.key, { remarks: v })} />
                </Cell>

                <Box sx={{ display: 'flex', alignItems: 'center',
                           justifyContent: 'center' }}>
                  <Button size="small" onClick={() => removeRow(r.key)}
                    aria-label={t('entry.removeLine')}
                    sx={{ minWidth: 0, color: c.inkMuted }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </Button>
                </Box>
              </Box>
            )
          })}
        </Box>

        <Stack direction="row" spacing={2} alignItems="center"
          sx={{ px: 2, py: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button size="small" startIcon={<AddIcon />} onClick={() => addRow()}>
            {t('entry.addLine')}
          </Button>
          <Keycap combo="alt+a" />
          {/* Shown, not hidden: a clerk learns these exist by seeing
              them, not by being told once during training. */}
          <Stack direction="row" spacing={0.75} alignItems="center"
            sx={{ display: { xs: 'none', md: 'flex' }, ml: 1 }}>
            <Typography sx={{ fontSize: 11.5, color: c.inkMuted }}>
              {t('quick.addNew')}
            </Typography>
            {([['alt+c', 'trip.customer'], ['alt+v', 'trip.vehicle'],
               ['alt+r', 'trip.route'], ['alt+n', 'trip.driver']] as const)
              .map(([combo, key]) => (
                <Stack key={combo} direction="row" spacing={0.4}
                  alignItems="center">
                  <Keycap combo={combo} />
                  <Typography sx={{ fontSize: 11.5, color: c.inkMuted }}>
                    {t(key)}
                  </Typography>
                </Stack>
              ))}
          </Stack>
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" sx={{ color: c.inkMuted }}>
            {t('trip.readyCount', { count: filled.length })}
          </Typography>
          <Button variant="contained" onClick={() => save.mutate()}
            disabled={save.isPending || !customer || filled.length === 0}>
            {save.isPending ? t('common.loading') : t('trip.save')}
          </Button>
        </Stack>
      </Paper>

      <CreatePartyDialog open={quick === 'customer'}
        defaultKind="customer"
        onClose={() => setQuick(null)}
        onCreated={(led) => {
          // Straight into the field that was empty.
          setCustomer(led.code)
          pending.current = null
        }} />

      <QuickVehicleDialog open={quick === 'vehicle'}
        onClose={() => setQuick(null)}
        onCreated={(v) => {
          void qc.invalidateQueries({ queryKey: ['vehicles'] })
          if (pending.current != null) {
            setRow(pending.current, { vehicle: v.plate_no })
          }
          backToRow('vehicle')
        }} />

      <QuickRouteDialog open={quick === 'route'}
        onClose={() => setQuick(null)}
        onCreated={(r) => {
          void qc.invalidateQueries({ queryKey: ['routes'] })
          const key = pending.current
          if (key != null) {
            setRow(key, { routeId: String(r.id) })
            // A new route's house rate is the only suggestion there is.
            if (r.default_rate) setRow(key, { rate: normalise(r.default_rate) })
          }
          backToRow('route')
        }} />

      <QuickDriverDialog open={quick === 'driver'}
        onClose={() => setQuick(null)}
        onCreated={(d) => {
          void qc.invalidateQueries({ queryKey: ['drivers'] })
          if (pending.current != null) {
            setRow(pending.current, { driver: d.name })
          }
          backToRow('driver')
        }} />

      <Snackbar open={!!saved} autoHideDuration={4000}
        onClose={() => setSaved(null)} message={saved ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Stack>
  )
}
